import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { Component, OnInit, OnDestroy, NgZone, ElementRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil, finalize } from 'rxjs/operators';
import type * as am5 from '@amcharts/amcharts5';
import type * as am5xy from '@amcharts/amcharts5/xy';
import { MatDialog, type MatDialogRef } from '@angular/material/dialog';
import { IDashboardWidget } from 'src/app/models/dashboard';
import { SampleWidgetService } from 'src/app/services/sample-widget.service';
import { CommonService } from 'src/app/services/common.service';
import { WidgetOrgFilterComponent } from 'src/app/common-components/widget-org-filter/widget-org-filter.component';
import { ChartService } from 'src/app/services/chart.service';
import {
    SampleCollectionMetricsRequest,
    SampleCollectionMetricsResponse,
    BreakdownItem
} from 'src/app/models/dashboard/sample-collection-metrics.model';
import { SampleMetricsStackBy } from 'src/app/constants/sample-metrics.constants';
import { SampleCollectionMetricsFiltersComponent } from './sample-collection-metrics-filters.component';
import {
    SampleCollectionMetricsFilterValues,
    PivotSortColumn,
    SAMPLE_METRICS_FILTER_DEFAULTS,
    DEFAULT_PIVOT_SORT_COLUMN,
    DEFAULT_PIVOT_SORT_ASC,
    configToFilterValues,
    filterValuesToConfig,
    isPivotSortColumn,
    clampGranularity,
    SAMPLE_METRICS_LEGEND_MAX_HEIGHT,
    SAMPLE_METRICS_LEGEND_LABEL_MAX_WIDTH,
    SAMPLE_METRICS_TOOLTIP_FILL
} from './sample-collection-metrics-widget.constants';
import { escapeChartMarkup } from 'src/app/shared/utils/analyst-chart-display.util';
import { BaseDashboardWidget } from 'src/app/components/dashboard/dashboard-widgets/_shared/base-dashboard-widget';
import { WidgetTableSort } from 'src/app/components/dashboard/dashboard-widgets/_shared/widget-table-sort';
import {
    SampleCollectionMetricsBreakdownDialogComponent,
    BreakdownDialogData
} from './sample-collection-metrics-breakdown-dialog.component';

interface BreakdownItemVm extends BreakdownItem {
    trendClass: string;
    trendIcon: string;
    formattedYoy: string;
}

interface PivotRow {
    id: string;
    name: string;
    periodTotals: Record<string, number>;
    total: number;
    yoyChangePercent: number | null;
    trendClass: string;
    formattedYoy: string;
    workerCount?: number;
    contractorCount?: number;
}

interface KpiVm {
    trendClass: string;
    trendIcon: string;
    formattedYoy: string;
}

interface ChartDataPoint {
    period: string;
    current: number;
    prior: number;
    [key: string]: string | number;
}

@Component({
    standalone: true,
    selector: 'app-sample-collection-metrics-widget',
    imports: [CommonModule, FormsModule, TranslateModule, SampleCollectionMetricsFiltersComponent, WidgetOrgFilterComponent],
    templateUrl: './sample-collection-metrics-widget.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./sample-collection-metrics-widget.component.scss']
})
export class SampleCollectionMetricsWidgetComponent
    extends BaseDashboardWidget
    implements OnInit, OnDestroy, IDashboardWidget
{
    @ViewChild('chartDiv', { static: false }) chartDiv!: ElementRef;

    data?: SampleCollectionMetricsResponse;
    kpiVm?: KpiVm;
    breakdownItems: BreakdownItemVm[] = [];
    pivotRows: PivotRow[] = [];
    pivotPeriods: string[] = [];

    readonly sortColumns = PivotSortColumn;
    readonly sort = new WidgetTableSort<PivotRow, string>(
        (row, column) => isPivotSortColumn(column) ? row[column] : row.periodTotals[column] ?? null,
        DEFAULT_PIVOT_SORT_COLUMN,
        DEFAULT_PIVOT_SORT_ASC,
    );

    filterValues: SampleCollectionMetricsFilterValues = { ...SAMPLE_METRICS_FILTER_DEFAULTS };

    private root?: am5.Root;
    private chart?: am5xy.XYChart;
    private requestVersion = 0;
    private breakdownDialogRef?: MatDialogRef<SampleCollectionMetricsBreakdownDialogComponent>;

    private readonly COLUMN_WIDTH_PCT = 60;
    private readonly TOOLTIP_DY = -65;
    private readonly TOOLTIP_ROW_STYLE = 'display:flex;justify-content:space-between;gap:8px;padding:1px 0;break-inside:avoid';
    private readonly TOOLTIP_NAME_STYLE = 'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#dfe6e9';
    private readonly TOOLTIP_VAL_STYLE = 'font-weight:600;flex-shrink:0;color:#ffffff';

    constructor(
        private sampleWidgetService: SampleWidgetService,
        private commonService: CommonService,
        private chartService: ChartService,
        private translate: TranslateService,
        private zone: NgZone,
        private dialog: MatDialog
    ) {
        super();
    }

    protected override syncFromConfig(): void {
        this.filterValues = configToFilterValues(this.widgetConfig);
    }

    protected override reload(): void {
        this.loadData();
    }

    protected override onDestroy(): void {
        this.disposeChart();
    }

    onFilterValuesChange(values: SampleCollectionMetricsFilterValues): void {
        this.filterValues = values;
        this.widgetConfig = { ...this.widgetConfig, ...filterValuesToConfig(values) };
        this.configChange.emit(this.widgetConfig);
        this.reload();
    }

    onFilterValuesReconcile(values: SampleCollectionMetricsFilterValues): void {
        this.filterValues = values;
        this.reload();
    }

    get stackByLabel(): string {
        return this.filterValues.selectedStackBy === SampleMetricsStackBy.Agent ? 'Agent' : 'SEG';
    }

    private openBreakdownModal(ctx: ChartDataPoint, stackNames: string[]): void {
        if (this.breakdownDialogRef) return;
        const rows = stackNames
            .map(n => ({ name: n, value: (ctx[n] as number) ?? 0 }))
            .filter(r => r.value > 0)
            .sort((a, b) => b.value - a.value);
        const data: BreakdownDialogData = {
            period: ctx.period,
            total: ctx.current,
            prior: ctx.prior > 0 ? ctx.prior : null,
            rows
        };
        this.breakdownDialogRef = this.dialog.open(SampleCollectionMetricsBreakdownDialogComponent, {
            data,
            panelClass: 'bd-breakdown-dialog-panel'
        });
        this.breakdownDialogRef.afterClosed().pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.breakdownDialogRef = undefined;
        });
    }

    trackByItemId(_index: number, item: { id: string }): string {
        return item.id;
    }

    sortBy(column: string): void {
        this.sort.toggle(column);
        this.pivotRows = this.sort.apply(this.pivotRows);
    }

    getTrendIcon(yoyChange: number | null): string {
        if (yoyChange == null || yoyChange === 0) return 'remove';
        return yoyChange > 0 ? 'arrow_upward' : 'arrow_downward';
    }

    getTrendClass(yoyChange: number | null): string {
        if (yoyChange == null || yoyChange === 0) return 'trend-neutral';
        return yoyChange > 0 ? 'trend-positive' : 'trend-negative';
    }

    formatYoy(value: number | null): string {
        if (value == null) return '';
        const sign = value > 0 ? '+' : '';
        return `${sign}${value}%`;
    }

    private loadData(): void {
        const currentVersion = ++this.requestVersion;
        this.isLoading = true;
        this.error = undefined;

        const effectiveOrgId = this.resolveEffectiveOrgIdAndRecord();

        const request: SampleCollectionMetricsRequest = {
            organizationLevelId: effectiveOrgId,
            timeRange: this.filterValues.selectedTimeRange,
            granularity: clampGranularity(
                this.filterValues.selectedGranularity,
                this.filterValues.selectedTimeRange,
                this.filterValues.customStartDate,
                this.filterValues.customEndDate
            ),
            stackBy: this.filterValues.selectedStackBy,
            agentGroupId: this.filterValues.selectedAgentGroupId ?? undefined,
            agentIds: this.filterValues.selectedAgentIds.length ? this.filterValues.selectedAgentIds : undefined,
            segIds: this.filterValues.selectedSegIds.length ? this.filterValues.selectedSegIds : undefined,
            surveyTypeIds: this.filterValues.selectedSurveyTypeIds.length ? this.filterValues.selectedSurveyTypeIds : undefined,
            customStartDate: this.filterValues.customStartDate ?? undefined,
            customEndDate: this.filterValues.customEndDate ?? undefined,
            shiftType: this.filterValues.selectedShiftType ?? undefined
        };

        this.sampleWidgetService.getCollectionMetrics(request)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    if (currentVersion === this.requestVersion) {
                        this.isLoading = false;
                    }
                })
            )
            .subscribe({
                next: (response) => {
                    if (currentVersion !== this.requestVersion) return;
                    this.data = response;
                    this.kpiVm = {
                        trendClass: this.getTrendClass(response.kpi.yoyChangePercent),
                        trendIcon: this.getTrendIcon(response.kpi.yoyChangePercent),
                        formattedYoy: this.formatYoy(response.kpi.yoyChangePercent),
                    };
                    this.breakdownItems = this.computeBreakdownItems(response);
                    const pivot = this.computePivotTable(response);
                    this.pivotPeriods = pivot.periods;
                    this.pivotRows = this.sort.apply(pivot.rows);
                    setTimeout(() => this.buildChart(), 0);
                },
                error: () => {
                    if (currentVersion !== this.requestVersion) return;
                    this.error = this.translate.instant('Failed to load sample collection metrics');
                    this.data = undefined;
                    this.kpiVm = undefined;
                    this.breakdownItems = [];
                }
            });
    }

    private computeBreakdownItems(response: SampleCollectionMetricsResponse): BreakdownItemVm[] {
        if (!response.timeSeries?.length) return [];

        const seriesMap = new Map<string, { name: string; currentYtd: number; priorYtd: number }>();

        for (const period of response.timeSeries) {
            for (const seriesItem of period.series) {
                const existing = seriesMap.get(seriesItem.id);
                if (existing) {
                    existing.currentYtd += seriesItem.count;
                    existing.priorYtd += seriesItem.priorCount ?? 0;
                } else {
                    seriesMap.set(seriesItem.id, {
                        name: seriesItem.name,
                        currentYtd: seriesItem.count,
                        priorYtd: seriesItem.priorCount ?? 0
                    });
                }
            }
        }

        return Array.from(seriesMap.entries()).map(([id, data]) => {
            const yoyChangePercent = data.priorYtd > 0
                ? Math.round(((data.currentYtd - data.priorYtd) / data.priorYtd) * 1000) / 10
                : null;
            return {
                id, name: data.name, currentYtd: data.currentYtd, priorYtd: data.priorYtd, yoyChangePercent,
                trendClass: this.getTrendClass(yoyChangePercent),
                trendIcon: this.getTrendIcon(yoyChangePercent),
                formattedYoy: this.formatYoy(yoyChangePercent),
            };
        });
    }

    private computePivotTable(response: SampleCollectionMetricsResponse): { periods: string[]; rows: PivotRow[] } {
        if (!response.timeSeries?.length) return { periods: [], rows: [] };

        const periods = response.timeSeries.map(p => p.period);
        const rowMap = new Map<string, PivotRow>();
        const isSeg = this.filterValues.selectedStackBy === SampleMetricsStackBy.Seg;
        const workforceMap = new Map(response.segWorkforce?.map(w => [w.segId, w]) ?? []);

        for (const period of response.timeSeries) {
            for (const seriesItem of period.series) {
                if (!rowMap.has(seriesItem.id)) {
                    const workforce = isSeg ? workforceMap.get(seriesItem.id) : undefined;
                    rowMap.set(seriesItem.id, {
                        id: seriesItem.id,
                        name: seriesItem.name,
                        periodTotals: {},
                        total: 0,
                        yoyChangePercent: null,
                        trendClass: 'trend-neutral',
                        formattedYoy: '',
                        workerCount: workforce?.numberOfWorkers,
                        contractorCount: workforce?.numberOfContractors,
                    });
                }
                const row = rowMap.get(seriesItem.id)!;
                row.periodTotals[period.period] = (row.periodTotals[period.period] ?? 0) + seriesItem.count;
                row.total += seriesItem.count;
            }
        }

        const priorTotals = new Map<string, number>();
        if (response.timeSeries.length > 0) {
            for (const period of response.timeSeries) {
                for (const seriesItem of period.series) {
                    priorTotals.set(seriesItem.id, (priorTotals.get(seriesItem.id) ?? 0) + (seriesItem.priorCount ?? 0));
                }
            }
        }

        const rows = Array.from(rowMap.values()).map(row => {
            const prior = priorTotals.get(row.id) ?? 0;
            const yoyChangePercent = prior > 0
                ? Math.round(((row.total - prior) / prior) * 1000) / 10
                : null;
            return {
                ...row,
                yoyChangePercent,
                trendClass: this.getTrendClass(yoyChangePercent),
                formattedYoy: this.formatYoy(yoyChangePercent),
            };
        });

        return { periods, rows };
    }

    get isStackBySeg(): boolean {
        return this.filterValues.selectedStackBy === SampleMetricsStackBy.Seg;
    }

    private async buildChart(): Promise<void> {
        if (!this.data?.timeSeries?.length || !this.chartDiv) return;

        const { am5: am5Ns, am5xy: am5xyNs } = await this.chartService.getAmCharts5();

        const stackNames = this.getUniqueStackNames();
        const chartData: ChartDataPoint[] = this.data.timeSeries.map(period => {
            const point: ChartDataPoint = {
                period: period.period,
                current: period.total,
                prior: period.priorTotal ?? 0
            };
            period.series.forEach(seriesItem => {
                point[seriesItem.name] = seriesItem.count;
            });
            return point;
        });

        this.zone.runOutsideAngular(() => {
            this.disposeChart();

            const root = am5Ns.Root.new(this.chartDiv.nativeElement);
            root.setThemes([]);
            root._logo?.dispose();

            const chart = root.container.children.push(am5xyNs.XYChart.new(root, {
                paddingRight: 20,
                layout: root.verticalLayout
            }));

            const xRenderer = am5xyNs.AxisRendererX.new(root, { minGridDistance: 30 });
            xRenderer.grid.template.set('location', 0);
            xRenderer.labels.template.setAll({ fontSize: 11, oversizedBehavior: 'truncate', maxWidth: 80 });
            const categoryAxis = chart.xAxes.push(am5xyNs.CategoryAxis.new(root, {
                categoryField: 'period',
                renderer: xRenderer
            }));
            categoryAxis.data.setAll(chartData);

            const valueAxis = chart.yAxes.push(am5xyNs.ValueAxis.new(root, {
                renderer: am5xyNs.AxisRendererY.new(root, {}),
                min: 0
            }));
            valueAxis.get('renderer').labels.template.setAll({ fontSize: 11 });

            let overlaySeries: am5xy.ColumnSeries | undefined;
            if (stackNames.length > 0) {
                // Stacked visual series — no individual tooltips
                stackNames.forEach(name => {
                    const series = chart.series.push(am5xyNs.ColumnSeries.new(root, {
                        name: escapeChartMarkup(name),
                        xAxis: categoryAxis,
                        yAxis: valueAxis,
                        valueYField: name,
                        categoryXField: 'period',
                        stacked: true
                    }));
                    series.columns.template.setAll({
                        width: am5Ns.percent(this.COLUMN_WIDTH_PCT),
                        cornerRadiusTL: 2,
                        cornerRadiusTR: 2
                    });
                    series.data.setAll(chartData);
                });

                // Transparent overlay series — captures hover and shows combined tooltip
                overlaySeries = chart.series.push(am5xyNs.ColumnSeries.new(root, {
                    xAxis: categoryAxis,
                    yAxis: valueAxis,
                    valueYField: 'current',
                    categoryXField: 'period',
                    stacked: false
                }));
                overlaySeries.columns.template.setAll({
                    width: am5Ns.percent(this.COLUMN_WIDTH_PCT),
                    fillOpacity: 0,
                    strokeOpacity: 0
                });
                this.configureStackedSeriesTooltip(root, am5Ns, overlaySeries, stackNames, am5Ns.color(SAMPLE_METRICS_TOOLTIP_FILL));
                overlaySeries.data.setAll(chartData);
            } else {
                const currentSeries = chart.series.push(am5xyNs.ColumnSeries.new(root, {
                    name: 'Current Period',
                    xAxis: categoryAxis,
                    yAxis: valueAxis,
                    valueYField: 'current',
                    categoryXField: 'period',
                    tooltip: am5Ns.Tooltip.new(root, { labelText: '{name}: {valueY}' })
                }));
                currentSeries.columns.template.setAll({
                    width: am5Ns.percent(40),
                    fill: am5Ns.color(0x6c5ce7),
                    cornerRadiusTL: 3,
                    cornerRadiusTR: 3
                });
                currentSeries.data.setAll(chartData);
            }

            const priorSeries = chart.series.push(am5xyNs.LineSeries.new(root, {
                name: 'Prior Period',
                xAxis: categoryAxis,
                yAxis: valueAxis,
                valueYField: 'prior',
                categoryXField: 'period',
                stroke: am5Ns.color(0xb2bec3),
                tooltip: am5Ns.Tooltip.new(root, { labelText: 'Prior Period: {valueY}' })
            }));
            priorSeries.strokes.template.setAll({
                strokeWidth: 2,
                stroke: am5Ns.color(0xb2bec3),
                strokeDasharray: [4, 4]
            });
            priorSeries.fills.template.setAll({ visible: false });
            priorSeries.bullets.push(() => am5Ns.Bullet.new(root, {
                sprite: am5Ns.Circle.new(root, {
                    radius: 4,
                    fill: am5Ns.color(0xb2bec3),
                    stroke: am5Ns.color(0xffffff)
                })
            }));
            priorSeries.data.setAll(chartData);

            chart.set('cursor', am5xyNs.XYCursor.new(root, { behavior: 'none', xAxis: categoryAxis }));

            const legend = chart.children.push(am5Ns.Legend.new(root, {
                x: am5Ns.p50,
                centerX: am5Ns.p50,
                maxHeight: SAMPLE_METRICS_LEGEND_MAX_HEIGHT
            }));
            this.enableLegendScrollbarOnOverflow(root, am5Ns, legend);
            legend.labels.template.setAll({
                fontSize: 11,
                maxWidth: SAMPLE_METRICS_LEGEND_LABEL_MAX_WIDTH,
                oversizedBehavior: 'truncate'
            });
            legend.itemContainers.template.setAll({
                tooltipText: '{name}',
                tooltip: this.createDarkTooltip(root, am5Ns, am5Ns.color(SAMPLE_METRICS_TOOLTIP_FILL))
            });
            legend.markers.template.setAll({ width: 10, height: 10 });
            legend.data.setAll([
                priorSeries,
                ...chart.series.values.filter(s => s !== overlaySeries && s !== priorSeries)
            ]);

            chart.plotContainer.set('interactive', true);
            if (overlaySeries) {
                const overlay = overlaySeries;
                chart.plotContainer.events.on('click', () => {
                    const ctx = overlay.get('tooltipDataItem')?.dataContext as ChartDataPoint | undefined;
                    if (ctx && stackNames.some(name => (ctx[name] as number) > 0)) {
                        this.zone.run(() => this.openBreakdownModal(ctx, stackNames));
                    }
                });
            }

            this.root = root;
            this.chart = chart;
        });
    }

    private getUniqueStackNames(): string[] {
        if (!this.data?.timeSeries) return [];
        const names = new Set<string>();
        this.data.timeSeries.forEach(period => {
            period.series.forEach(seriesItem => names.add(seriesItem.name));
        });
        return Array.from(names);
    }

    private buildCombinedTooltip(ctx: ChartDataPoint, stackNames: string[], maxItems?: number, multiCol = false): string {
        const allRows = stackNames
            .map(stackName => ({ stackName, value: (ctx[stackName] as number) ?? 0 }))
            .filter(r => r.value > 0)
            .sort((a, b) => b.value - a.value);

        const rows = maxItems != null ? allRows.slice(0, maxItems) : allRows;
        const remaining = allRows.length - rows.length;
        const escapedPeriod = this.escapeHtml(String(ctx.period));

        let html = `<div style="font-size:12px;padding:2px 0;min-width:180px;max-width:${multiCol ? '480px' : '260px'}">`;
        html += `<div style="font-weight:700;margin-bottom:5px;padding-bottom:4px;border-bottom:1px solid rgba(255,255,255,0.25);color:#ffffff">${escapedPeriod}</div>`;
        html += `<div style="font-weight:600;margin-bottom:6px;color:#dfe6e9">${this.translate.instant('Total:')} ${ctx.current}</div>`;

        if (multiCol) {
            html += `<div style="column-count:2;column-gap:14px;column-rule:1px solid rgba(255,255,255,0.1)">`;
        }
        for (const r of rows) {
            const escapedName = this.escapeHtml(r.stackName);
            html += `<div style="${this.TOOLTIP_ROW_STYLE}"><span style="${this.TOOLTIP_NAME_STYLE}">${escapedName}</span><span style="${this.TOOLTIP_VAL_STYLE}">${r.value}</span></div>`;
        }
        if (multiCol) {
            html += '</div>';
        }

        if (remaining > 0) {
            html += `<div style="margin-top:6px;padding-top:5px;border-top:1px solid rgba(255,255,255,0.25);color:#ffffff;font-weight:700;font-size:13px">+${remaining} ${this.translate.instant('more — click for full list')}</div>`;
        }

        if (ctx.prior > 0) {
            html += `<div style="margin-top:6px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.2);color:#b2bec3">${this.translate.instant('Prior Period:')} ${ctx.prior}</div>`;
        }
        html += '</div>';
        return html;
    }

    private escapeHtml(text: string): string {
        return text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/\[/g, '&#91;')
            .replace(/\]/g, '&#93;')
            .replace(/\{/g, '&#123;')
            .replace(/\}/g, '&#125;');
    }

    private configureStackedSeriesTooltip(
        root: am5.Root,
        am5Ns: typeof import('@amcharts/amcharts5'),
        series: am5xy.ColumnSeries,
        stackNames: string[],
        fill: am5.Color
    ): void {
        const tooltip = this.createDarkTooltip(root, am5Ns, fill, {
            pointerOrientation: 'vertical',
            dy: this.TOOLTIP_DY
        });
        tooltip.label.setAll({ oversizedBehavior: 'wrap', maxWidth: 500 });
        series.set('tooltip', tooltip);

        tooltip.adapters.add('labelHTML', (_html, target) => {
            const ctx = target.dataItem?.dataContext as ChartDataPoint | undefined;
            if (!ctx) return '';
            const w = this.chartDiv.nativeElement.offsetWidth;
            // 2 columns only for very wide widgets (≥1100px ≈ grid width 4+)
            const useMultiCol = w >= 1100;
            const maxItems = useMultiCol
                ? Math.max(6, Math.floor(w / 70))    // 2 cols wide widget
                : Math.max(2, Math.floor(w / 150));  // 1 col narrow widget
            // Shift tooltip right on narrow widgets so it doesn't cover the bar
            tooltip.set('dx', w < 800 ? 25 : 0);
            return this.buildCombinedTooltip(ctx, stackNames, maxItems, useMultiCol);
        });
    }

    private enableLegendScrollbarOnOverflow(
        root: am5.Root,
        am5Ns: typeof import('@amcharts/amcharts5'),
        legend: am5.Legend
    ): void {
        legend.events.on('boundschanged', () => {
            const contentHeight = Math.max(0, ...legend.itemContainers.values.map(item => item.y() + item.height()));
            const overflows = contentHeight > SAMPLE_METRICS_LEGEND_MAX_HEIGHT;
            const hasScrollbar = !!legend.get('verticalScrollbar');
            if (overflows && !hasScrollbar) {
                legend.set('verticalScrollbar', am5Ns.Scrollbar.new(root, { orientation: 'vertical' }));
            } else if (!overflows && hasScrollbar) {
                legend.set('verticalScrollbar', undefined);
            }
        });
    }

    private createDarkTooltip(
        root: am5.Root,
        am5Ns: typeof import('@amcharts/amcharts5'),
        fill: am5.Color,
        settings: Partial<am5.ITooltipSettings> = {}
    ): am5.Tooltip {
        const tooltip = am5Ns.Tooltip.new(root, {
            getFillFromSprite: false,
            autoTextColor: false,
            ...settings
        });
        (tooltip.get('background') as am5.RoundedRectangle | undefined)?.setAll({
            fill,
            cornerRadiusTL: 6,
            cornerRadiusTR: 6,
            cornerRadiusBL: 6,
            cornerRadiusBR: 6
        });
        tooltip.label.set('fill', am5Ns.color(0xffffff));
        return tooltip;
    }

    private disposeChart(): void {
        if (this.root) {
            this.root.dispose();
            this.root = undefined;
            this.chart = undefined;
        }
        this.breakdownDialogRef?.close();
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;
}
